import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import MovieCard from "@/components/feature/MovieCard";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { getMoviesByType, getTypeLabel, searchMovies } from "@/lib/content";
import type { Movie } from "@/types/content";
import { FilterGroup, SortBar } from "./components/FilterBar";

const PAGE_SIZE = 8;

const REGION_OPTIONS = ["全部", "中国大陆", "中国香港"];

const sortOptions = [
  { label: "综合", value: "comprehensive" },
  { label: "最新", value: "newest" },
  { label: "评分", value: "rating" },
  { label: "热门", value: "hot" },
];

const ratingRanges: { label: string; min: number }[] = [
  { label: "全部", min: 0 },
  { label: "9.0 分以上", min: 9 },
  { label: "8.5 分以上", min: 8.5 },
  { label: "8.0 分以上", min: 8 },
];

export default function CategoryPage() {
  const { type = "all" } = useParams();
  const [searchParams] = useSearchParams();
  const keyword = searchParams.get("q") ?? "";

  const typeLabel = getTypeLabel(type);
  const yearOptions = useMemo(() => {
    const base = getMoviesByType(type);
    const years = Array.from(new Set(base.map((m) => String(m.year)))).sort((a, b) => Number(b) - Number(a));
    return ["全部", ...years];
  }, [type]);

  const genreOptions = useMemo(() => {
    const base = getMoviesByType(type);
    const genres = Array.from(new Set(base.flatMap((m) => m.genres)));
    return ["全部", ...genres];
  }, [type]);

  const [region, setRegion] = useState("全部");
  const [year, setYear] = useState("全部");
  const [rating, setRating] = useState("全部");
  const [genre, setGenre] = useState("全部");
  const [sort, setSort] = useState("comprehensive");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setRegion("全部");
    setYear("全部");
    setRating("全部");
    setGenre("全部");
    setSort("comprehensive");
    setPage(1);
  }, [type, keyword]);

  useEffect(() => {
    setPage(1);
  }, [region, year, rating, genre, sort]);

  const keywordMatched = useMemo(() => {
    const base = keyword ? searchMovies(keyword) : getMoviesByType(type);
    const allowedType = type === "movie" || type === "tv" ? type : null;
    return keyword ? base.filter((m) => (allowedType ? m.type === allowedType : true)) : base;
  }, [keyword, type]);

  const filtered = useMemo(() => {
    const minRating = ratingRanges.find((r) => r.label === rating)?.min ?? 0;
    const list = keywordMatched.filter((m) => {
      if (region !== "全部" && m.region !== region) return false;
      if (year !== "全部" && String(m.year) !== year) return false;
      if (genre !== "全部" && !m.genres.includes(genre)) return false;
      if (m.rating < minRating) return false;
      return true;
    });
    return sortMovies(list, sort);
  }, [keywordMatched, region, year, genre, rating, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const resetFilters = () => {
    setRegion("全部");
    setYear("全部");
    setRating("全部");
    setGenre("全部");
    setSort("comprehensive");
    setPage(1);
  };

  const meta = buildMeta(type, typeLabel, keyword);
  useDocumentMeta(meta);

  const description =
    type === "movie"
      ? "浏览精选电影资源，支持按地区、年代、评分与类型多维筛选，快速找到值得一看的院线与口碑佳作。"
      : type === "tv"
        ? "浏览热门与口碑电视剧，支持按地区、年代、评分与类型多维筛选，追剧更高效。"
        : "浏览全部电影与电视剧资源，支持按地区、年代、评分与类型多维筛选，发现你的下一部心头好。";

  return (
    <Layout>
      <section className="w-full bg-primary-950 text-background-50">
        <div className="w-full max-w-[1400px] mx-auto px-4 md:px-8 py-8 md:py-10">
          <nav className="text-xs md:text-sm text-background-400 mb-4 flex items-center gap-1.5 flex-wrap">
            <Link to="/" className="hover:text-accent-400 transition-colors cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-background-400">影视分类</span>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-accent-400">{typeLabel}</span>
          </nav>
          <h1 className="text-2xl md:text-4xl font-bold font-heading">
            {typeLabel}
            {keyword && <span className="text-background-300 text-lg md:text-2xl ml-2">“{keyword}” 的搜索结果</span>}
          </h1>
          <p className="mt-2 text-sm md:text-base text-background-300 max-w-2xl">{description}</p>
          <p className="mt-3 text-xs text-background-500">
            共找到 {filtered.length} 部作品 · 搜索与筛选为演示用途，数据非实时
          </p>
        </div>
      </section>

      <div className="w-full max-w-[1400px] mx-auto px-4 md:px-8 py-8 md:py-12">
        <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5 mb-6">
          <FilterGroup label="地区" options={REGION_OPTIONS} value={region} onChange={setRegion} />
          <FilterGroup label="年代" options={yearOptions} value={year} onChange={setYear} />
          <FilterGroup label="评分" options={ratingRanges.map((r) => r.label)} value={rating} onChange={setRating} />
          <FilterGroup label="类型" options={genreOptions} value={genre} onChange={setGenre} />
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-4">
            <SortBar options={sortOptions} value={sort} onChange={setSort} />
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex w-fit items-center gap-1.5 px-4 py-2 rounded-md border border-background-300 text-sm text-foreground-600 hover:border-accent-400 hover:text-accent-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-refresh-line"></i>
              重置筛选
            </button>
          </div>
        </div>

        {paged.length > 0 ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
              {paged.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="mt-10 flex items-center justify-center gap-2">
                <button
                  type="button"
                  disabled={page === 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="w-9 h-9 flex items-center justify-center rounded-md border border-background-300 text-foreground-600 hover:border-primary-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-s-line"></i>
                </button>
                {Array.from({ length: totalPages }).map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setPage(i + 1)}
                    className={`w-9 h-9 rounded-md text-sm transition-colors cursor-pointer ${
                      page === i + 1
                        ? "bg-primary-600 text-background-50 font-medium"
                        : "border border-background-300 text-foreground-600 hover:border-primary-400"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="w-9 h-9 flex items-center justify-center rounded-md border border-background-300 text-foreground-600 hover:border-primary-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="py-20 text-center bg-background-100 rounded-lg border border-background-200">
            <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
              <i className="ri-movie-2-line text-3xl"></i>
            </span>
            <h3 className="mt-4 text-lg font-semibold text-foreground-800">暂无符合条件的影视资源</h3>
            <p className="mt-1.5 text-sm text-foreground-500">试试调整筛选条件，或重置后重新浏览</p>
            <button
              type="button"
              onClick={resetFilters}
              className="mt-5 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-accent-500 text-primary-950 text-sm font-medium hover:bg-accent-400 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-refresh-line"></i>
              重置筛选
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
}

function sortMovies(list: Movie[], sort: string): Movie[] {
  const copy = [...list];
  switch (sort) {
    case "newest":
      return copy.sort((a, b) => b.year - a.year);
    case "rating":
      return copy.sort((a, b) => b.rating - a.rating);
    case "hot":
      return copy.sort((a, b) => b.popularity - a.popularity);
    default:
      return copy.sort((a, b) => b.popularity * 0.6 + b.rating * 10 - (a.popularity * 0.6 + a.rating * 10));
  }
}

function buildMeta(type: string, typeLabel: string, keyword: string) {
  if (keyword) {
    return {
      title: `“${keyword}” 搜索结果 - ${typeLabel} - 国语对白做受69`,
      description: `国语对白做受69为你展示与“${keyword}”相关的${typeLabel}资源，支持地区、年代、评分等多维筛选，方便快速查找影视作品信息。`,
      keywords: `${keyword},${typeLabel},影视资源,国语对白做受69`,
    };
  }
  if (type === "movie") {
    return {
      title: "电影 - 影视资源与推荐 - 国语对白做受69",
      description:
        "国语对白做受69电影分类页，汇集历史、科幻、悬疑、都市等多类型电影资源，支持地区、年代、评分与类型筛选，帮助你快速找到想看的好电影。",
      keywords: "电影,电影推荐,影视资源,国产电影,国语对白做受69",
    };
  }
  if (type === "tv") {
    return {
      title: "电视剧 - 影视资源与推荐 - 国语对白做受69",
      description:
        "国语对白做受69电视剧分类页，汇集古装、悬疑、都市、武侠等热门剧集资源，支持地区、年代、评分与类型筛选，追剧更高效。",
      keywords: "电视剧,剧集推荐,影视资源,国产剧,国语对白做受69",
    };
  }
  return {
    title: "全部影视资源 - 影视资源与推荐 - 国语对白做受69",
    description:
      "国语对白做受69全部影视资源页，涵盖电影与电视剧，支持地区、年代、评分与类型多维筛选，快速浏览并了解影视作品信息。",
    keywords: "影视资源,电影,电视剧,影视推荐,国语对白做受69",
  };
}